import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  RectangleStackIcon,
  PlayCircleIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TvIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  UserGroupIcon,
  PlusCircleIcon,
  PresentationChartLineIcon,
  FilmIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { getMyChannels } from '@/services/channelApi';
import type { Channel } from '@/types/channel';
import fullLogoSrc from '@/assets/branding/source/pointetracer-full-logo.png';
import wordmarkSrc from '@/assets/branding/wordmark.png';

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  exact?: boolean;
}

/**
 * Returns navigation items based on user role.
 * Both roles currently see the same items.
 * Coach-specific or athlete-specific items will be added here in Batch 5.
 */
function getNavItems(_role: string): NavItem[] {
  return [
    { label: 'Home', href: '/', icon: HomeIcon, exact: true },
    { label: 'Templates', href: '/templates', icon: RectangleStackIcon },
    // 'Create' entry removed: Templates page Create button + channel-context Create are the canonical entry points.
    // The /templates/create route still exists — existing bookmarks continue to work.
    { label: 'Evaluate', href: '/evaluate', icon: PlayCircleIcon },
    { label: 'Results', href: '/evaluations', icon: ChartBarIcon },
    { label: 'Search', href: '/evaluations/search', icon: MagnifyingGlassIcon },
    { label: 'Library', href: '/library', icon: FilmIcon },
    // S21: channel discovery — all roles (athletes drive the funnel; coaches/admins may also browse).
    // Uses GlobeAltIcon to avoid collision with MagnifyingGlassIcon already used by Search.
    { label: 'Discover', href: '/discover', icon: GlobeAltIcon },
  ];
}

const COLLAPSED_KEY = 'sidebar_collapsed';

function isNavItemActive(item: NavItem, pathname: string): boolean {
  if (item.exact) {
    return pathname === item.href;
  }
  return pathname === item.href || pathname.startsWith(item.href + '/');
}

function Sidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const navItems = getNavItems(user?.role ?? 'coach');
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const channelActive = location.pathname.startsWith('/channels');

  // Fetch the user's channels so the sidebar reflects what they actually own
  // or are members of (athletes see their member-channels via getMyChannels).
  const [channels, setChannels] = useState<Channel[] | null>(null);
  useEffect(() => {
    if (!user) {
      setChannels([]);
      return;
    }
    let cancelled = false;
    getMyChannels()
      .then((list) => {
        if (!cancelled) setChannels(list);
      })
      .catch(() => {
        if (!cancelled) setChannels([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, String(collapsed));
    } catch {
      // localStorage may not be available in some environments
    }
  }, [collapsed]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const sidebarContent = (
    <div
      className="flex flex-col h-full"
      style={{
        backgroundColor: 'var(--color-dark-elevated-1)',
        borderRight: '1px solid var(--color-border-subtle)',
        width: collapsed ? '76px' : '240px',
        transition: 'width 220ms ease',
        flexShrink: 0,
      }}
    >
      {/* Logo area */}
      <div
        className="flex items-center h-[72px] px-4"
        style={{ borderBottom: '1px solid var(--color-border-subtle)', flexShrink: 0 }}
      >
        <Link
          to="/"
          className="flex items-center gap-2 overflow-hidden"
          aria-label="PointeTracer home"
        >
          {/* Collapsed: icon only; Expanded: official wordmark image */}
          {collapsed ? (
            <img
              src={fullLogoSrc}
              alt="PointeTracer"
              style={{ width: '48px', height: '48px', objectFit: 'contain', flexShrink: 0 }}
            />
          ) : (
            <img
              src={wordmarkSrc /* dark-only */}
              alt="PointeTracer"
              style={{ height: '64px', width: 'auto', objectFit: 'contain', flexShrink: 0 }}
            />
          )}
        </Link>
      </div>

      {/* Main navigation */}
      <nav className="flex-1 py-4 overflow-y-auto" aria-label="Main navigation">
        <ul className="space-y-1 px-2">
          {navItems.map((item) => {
            const active = isNavItemActive(item, location.pathname);
            const Icon = item.icon;

            return (
              <li key={item.href}>
                <Link
                  to={item.href}
                  title={collapsed ? item.label : undefined}
                  aria-current={active ? 'page' : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: collapsed ? '0' : '10px',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    padding: collapsed ? '10px 0' : '10px 12px',
                    borderRadius: '10px',
                    borderLeft: active ? '3px solid var(--color-accent)' : '3px solid transparent',
                    backgroundColor: active ? 'var(--color-dark-elevated-2)' : 'transparent',
                    transition: 'background-color 120ms ease, border-color 120ms ease',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (!active) {
                      (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'var(--color-dark-elevated-2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <Icon
                    style={{
                      width: '20px',
                      height: '20px',
                      flexShrink: 0,
                      color: active ? 'var(--color-accent)' : 'var(--color-text-muted)',
                    }}
                    aria-hidden="true"
                  />
                  {!collapsed && (
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                      }}
                    >
                      {item.label}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Channels section */}
      <div style={{ padding: '0 16px', flexShrink: 0 }}>
        <div style={{ height: '1px', backgroundColor: 'var(--color-border-subtle)' }} />
        {!collapsed && (
          <span
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--color-text-muted)',
              padding: '12px 12px 4px',
            }}
          >
            Channels
          </span>
        )}
        {collapsed && <div style={{ paddingTop: '8px' }} />}
      </div>
      <nav className="py-1 pb-3" aria-label="Channel navigation">
        <ul className="space-y-1 px-2">
          {/* Render the user's actual channels; fall back to a "Create" link
              when they don't have any yet. Athletes see their member-channels. */}
          {channels && channels.length > 0
            ? channels.map((c) => {
                const target = `/channels/${c.slug}`;
                const active = location.pathname === target || location.pathname.startsWith(target + '/');
                return (
                  <li key={c.slug}>
                    <Link
                      to={target}
                      title={collapsed ? c.display_name : undefined}
                      aria-current={active ? 'page' : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: collapsed ? '0' : '10px',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        padding: collapsed ? '10px 0' : '10px 12px',
                        borderRadius: '10px',
                        borderLeft: active ? '3px solid var(--color-accent)' : '3px solid transparent',
                        backgroundColor: active ? 'var(--color-dark-elevated-2)' : 'transparent',
                        transition: 'background-color 120ms ease, border-color 120ms ease',
                        textDecoration: 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'var(--color-dark-elevated-2)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <TvIcon
                        style={{
                          width: '20px',
                          height: '20px',
                          flexShrink: 0,
                          color: active ? 'var(--color-accent)' : 'var(--color-text-muted)',
                        }}
                        aria-hidden="true"
                      />
                      {!collapsed && (
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                          }}
                        >
                          {c.display_name}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })
            : null}

          {/* Coaches/admins with no channels yet see a Create entry. */}
          {channels !== null && channels.length === 0 && user?.role !== 'athlete' && (
            <li>
              <Link
                to="/channels/create"
                title={collapsed ? 'Create channel' : undefined}
                aria-current={location.pathname === '/channels/create' ? 'page' : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: collapsed ? '0' : '10px',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  padding: collapsed ? '10px 0' : '10px 12px',
                  borderRadius: '10px',
                  borderLeft: channelActive ? '3px solid var(--color-accent)' : '3px solid transparent',
                  backgroundColor: channelActive ? 'var(--color-dark-elevated-2)' : 'transparent',
                  transition: 'background-color 120ms ease, border-color 120ms ease',
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => {
                  if (!channelActive) {
                    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'var(--color-dark-elevated-2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!channelActive) {
                    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                  }
                }}
              >
                <PlusCircleIcon
                  style={{
                    width: '20px',
                    height: '20px',
                    flexShrink: 0,
                    color: 'var(--color-text-muted)',
                  }}
                  aria-hidden="true"
                />
                {!collapsed && (
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    Create channel
                  </span>
                )}
              </Link>
            </li>
          )}
        </ul>
      </nav>

      {/* Admin section — only visible for admin role */}
      {user?.role === 'admin' && (
        <>
          <div style={{ padding: '0 16px', flexShrink: 0 }}>
            <div style={{ height: '1px', backgroundColor: 'var(--color-border-subtle)' }} />
            {!collapsed && (
              <span
                style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--color-accent-gold)',
                  padding: '12px 12px 4px',
                  opacity: 0.8,
                }}
              >
                Admin
              </span>
            )}
            {collapsed && <div style={{ paddingTop: '8px' }} />}
          </div>
          <nav className="py-1 pb-3" aria-label="Admin navigation">
            <ul className="space-y-1 px-2">
              {/* Admin overview — the main /admin page */}
              {(() => {
                const active = location.pathname === '/admin';
                return (
                  <li>
                    <Link
                      to="/admin"
                      title={collapsed ? 'Admin Overview' : undefined}
                      aria-current={active ? 'page' : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: collapsed ? '0' : '10px',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        padding: collapsed ? '10px 0' : '10px 12px',
                        borderRadius: '10px',
                        borderLeft: active ? '3px solid var(--color-accent-gold)' : '3px solid transparent',
                        backgroundColor: active ? 'color-mix(in srgb, var(--color-accent-gold) 8%, transparent)' : 'transparent',
                        transition: 'background-color 120ms ease, border-color 120ms ease',
                        textDecoration: 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'color-mix(in srgb, var(--color-accent-gold) 6%, transparent)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <ShieldCheckIcon
                        style={{
                          width: '20px',
                          height: '20px',
                          flexShrink: 0,
                          color: active ? 'var(--color-accent-gold)' : 'var(--color-text-muted)',
                        }}
                        aria-hidden="true"
                      />
                      {!collapsed && (
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            color: active ? 'var(--color-accent-gold)' : 'var(--color-text-secondary)',
                          }}
                        >
                          Overview
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })()}

              {/* Admin Users — /admin/users */}
              {(() => {
                const active = location.pathname === '/admin/users' || location.pathname.startsWith('/admin/users/');
                return (
                  <li>
                    <Link
                      to="/admin/users"
                      title={collapsed ? 'Admin Users' : undefined}
                      aria-current={active ? 'page' : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: collapsed ? '0' : '10px',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        padding: collapsed ? '10px 0' : '10px 12px',
                        borderRadius: '10px',
                        borderLeft: active ? '3px solid var(--color-accent-gold)' : '3px solid transparent',
                        backgroundColor: active ? 'color-mix(in srgb, var(--color-accent-gold) 8%, transparent)' : 'transparent',
                        transition: 'background-color 120ms ease, border-color 120ms ease',
                        textDecoration: 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'color-mix(in srgb, var(--color-accent-gold) 6%, transparent)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <ShieldCheckIcon
                        style={{
                          width: '20px',
                          height: '20px',
                          flexShrink: 0,
                          color: active ? 'var(--color-accent-gold)' : 'var(--color-text-muted)',
                        }}
                        aria-hidden="true"
                      />
                      {!collapsed && (
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            color: active ? 'var(--color-accent-gold)' : 'var(--color-text-secondary)',
                          }}
                        >
                          Users
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })()}

              {/* Admin Invitations — /admin/invitations */}
              {(() => {
                const active = location.pathname === '/admin/invitations' || location.pathname.startsWith('/admin/invitations/');
                return (
                  <li>
                    <Link
                      to="/admin/invitations"
                      title={collapsed ? 'Admin Invitations' : undefined}
                      aria-current={active ? 'page' : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: collapsed ? '0' : '10px',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        padding: collapsed ? '10px 0' : '10px 12px',
                        borderRadius: '10px',
                        borderLeft: active ? '3px solid var(--color-accent-gold)' : '3px solid transparent',
                        backgroundColor: active ? 'color-mix(in srgb, var(--color-accent-gold) 8%, transparent)' : 'transparent',
                        transition: 'background-color 120ms ease, border-color 120ms ease',
                        textDecoration: 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'color-mix(in srgb, var(--color-accent-gold) 6%, transparent)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <ShieldCheckIcon
                        style={{
                          width: '20px',
                          height: '20px',
                          flexShrink: 0,
                          color: active ? 'var(--color-accent-gold)' : 'var(--color-text-muted)',
                        }}
                        aria-hidden="true"
                      />
                      {!collapsed && (
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            color: active ? 'var(--color-accent-gold)' : 'var(--color-text-secondary)',
                          }}
                        >
                          Invitations
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })()}

              {/* Admin Metrics — /admin/metrics */}
              {(() => {
                const active = location.pathname === '/admin/metrics';
                return (
                  <li>
                    <Link
                      to="/admin/metrics"
                      title={collapsed ? 'Platform Metrics' : undefined}
                      aria-current={active ? 'page' : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: collapsed ? '0' : '10px',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        padding: collapsed ? '10px 0' : '10px 12px',
                        borderRadius: '10px',
                        borderLeft: active ? '3px solid var(--color-accent-gold)' : '3px solid transparent',
                        backgroundColor: active ? 'color-mix(in srgb, var(--color-accent-gold) 8%, transparent)' : 'transparent',
                        transition: 'background-color 120ms ease, border-color 120ms ease',
                        textDecoration: 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'color-mix(in srgb, var(--color-accent-gold) 6%, transparent)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <PresentationChartLineIcon
                        style={{
                          width: '20px',
                          height: '20px',
                          flexShrink: 0,
                          color: active ? 'var(--color-accent-gold)' : 'var(--color-text-muted)',
                        }}
                        aria-hidden="true"
                      />
                      {!collapsed && (
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            color: active ? 'var(--color-accent-gold)' : 'var(--color-text-secondary)',
                          }}
                        >
                          Metrics
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })()}
            </ul>
          </nav>
        </>
      )}

      {/* Coach Settings section — only visible for coach role (not admins) */}
      {user?.role === 'coach' && (
        <>
          <div style={{ padding: '0 16px', flexShrink: 0 }}>
            <div style={{ height: '1px', backgroundColor: 'var(--color-border-subtle)' }} />
            {!collapsed && (
              <span
                style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--color-accent-blue)',
                  padding: '12px 12px 4px',
                  opacity: 0.85,
                }}
              >
                Coach Settings
              </span>
            )}
            {collapsed && <div style={{ paddingTop: '8px' }} />}
          </div>
          <nav className="py-1 pb-3" aria-label="Coach settings navigation">
            <ul className="space-y-1 px-2">
              {(() => {
                const active =
                  location.pathname === '/coach/invitations' ||
                  location.pathname.startsWith('/coach/invitations/');
                return (
                  <li>
                    <Link
                      to="/coach/invitations"
                      title={collapsed ? 'Invitations' : undefined}
                      aria-current={active ? 'page' : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: collapsed ? '0' : '10px',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        padding: collapsed ? '10px 0' : '10px 12px',
                        borderRadius: '10px',
                        borderLeft: active
                          ? '3px solid var(--color-accent-blue)'
                          : '3px solid transparent',
                        backgroundColor: active ? 'color-mix(in srgb, var(--color-accent-blue) 8%, transparent)' : 'transparent',
                        transition: 'background-color 120ms ease, border-color 120ms ease',
                        textDecoration: 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLAnchorElement).style.backgroundColor =
                            'color-mix(in srgb, var(--color-accent-blue) 6%, transparent)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLAnchorElement).style.backgroundColor =
                            'transparent';
                        }
                      }}
                    >
                      <EnvelopeIcon
                        style={{
                          width: '20px',
                          height: '20px',
                          flexShrink: 0,
                          color: active
                            ? 'var(--color-accent-blue)'
                            : 'var(--color-text-muted)',
                        }}
                        aria-hidden="true"
                      />
                      {!collapsed && (
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            color: active
                              ? 'var(--color-accent-blue)'
                              : 'var(--color-text-secondary)',
                          }}
                        >
                          Invitations
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })()}

              {/* My Athletes — only shown when coach has ≥1 channel */}
              {channels && channels.length > 0 && (() => {
                const active =
                  location.pathname === '/athletes' ||
                  location.pathname.startsWith('/athletes/');
                return (
                  <li>
                    <Link
                      to="/athletes"
                      title={collapsed ? 'My Athletes' : undefined}
                      aria-current={active ? 'page' : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: collapsed ? '0' : '10px',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        padding: collapsed ? '10px 0' : '10px 12px',
                        borderRadius: '10px',
                        borderLeft: active
                          ? '3px solid var(--color-accent-blue)'
                          : '3px solid transparent',
                        backgroundColor: active ? 'color-mix(in srgb, var(--color-accent-blue) 8%, transparent)' : 'transparent',
                        transition: 'background-color 120ms ease, border-color 120ms ease',
                        textDecoration: 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLAnchorElement).style.backgroundColor =
                            'color-mix(in srgb, var(--color-accent-blue) 6%, transparent)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          (e.currentTarget as HTMLAnchorElement).style.backgroundColor =
                            'transparent';
                        }
                      }}
                    >
                      <UserGroupIcon
                        style={{
                          width: '20px',
                          height: '20px',
                          flexShrink: 0,
                          color: active
                            ? 'var(--color-accent-blue)'
                            : 'var(--color-text-muted)',
                        }}
                        aria-hidden="true"
                      />
                      {!collapsed && (
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            color: active
                              ? 'var(--color-accent-blue)'
                              : 'var(--color-text-secondary)',
                          }}
                        >
                          My Athletes
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })()}
            </ul>
          </nav>
        </>
      )}

      {/* Collapse toggle */}
      <div
        style={{ borderTop: '1px solid var(--color-border-subtle)', padding: '12px', flexShrink: 0 }}
      >
        <button
          type="button"
          onClick={() => setCollapsed((prev) => !prev)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            padding: '8px',
            borderRadius: '10px',
            border: 'none',
            backgroundColor: 'transparent',
            color: 'var(--color-text-muted)',
            cursor: 'pointer',
            transition: 'background-color 120ms ease, color 120ms ease',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-dark-elevated-2)';
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-primary)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-muted)';
          }}
        >
          {collapsed ? (
            <ChevronRightIcon style={{ width: '16px', height: '16px' }} aria-hidden="true" />
          ) : (
            <ChevronLeftIcon style={{ width: '16px', height: '16px' }} aria-hidden="true" />
          )}
          {!collapsed && (
            <span style={{ fontSize: '13px', marginLeft: '8px', whiteSpace: 'nowrap' }}>
              Collapse
            </span>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden lg:flex h-screen sticky top-0" aria-hidden="false">
        {sidebarContent}
      </div>

      {/* Mobile: hamburger button */}
      <button
        type="button"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg"
        style={{
          backgroundColor: 'var(--color-dark-elevated-1)',
          border: '1px solid var(--color-border-subtle)',
          color: 'var(--color-text-secondary)',
        }}
        onClick={() => setMobileOpen((prev) => !prev)}
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileOpen}
      >
        <svg
          style={{ width: '20px', height: '20px' }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          {mobileOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Mobile: overlay + drawer */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 z-40"
            style={{ backgroundColor: 'var(--color-dark-overlay)' }}
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          {/* Drawer */}
          <div className="lg:hidden fixed inset-y-0 left-0 z-50 flex h-screen">
            {/* Force expanded on mobile */}
            <div
              className="flex flex-col h-full"
              style={{
                backgroundColor: 'var(--color-dark-elevated-1)',
                borderRight: '1px solid var(--color-border-subtle)',
                width: '240px',
              }}
            >
              {/* Logo area */}
              <div
                className="flex items-center h-[72px] px-4"
                style={{ borderBottom: '1px solid var(--color-border-subtle)' }}
              >
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  aria-label="PointeTracer home"
                >
                  <img
                    src={wordmarkSrc /* dark-only */}
                    alt="PointeTracer"
                    style={{ height: '64px', width: 'auto', objectFit: 'contain' }}
                  />
                </Link>
              </div>

              {/* Nav */}
              <nav className="flex-1 py-4 overflow-y-auto px-2" aria-label="Main navigation">
                <ul className="space-y-1">
                  {navItems.map((item) => {
                    const active = isNavItemActive(item, location.pathname);
                    const Icon = item.icon;
                    return (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          aria-current={active ? 'page' : undefined}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            minHeight: '44px',
                            borderRadius: '10px',
                            borderLeft: active ? '3px solid var(--color-accent)' : '3px solid transparent',
                            backgroundColor: active ? 'var(--color-dark-elevated-2)' : 'transparent',
                            textDecoration: 'none',
                          }}
                        >
                          <Icon style={{ width: '20px', height: '20px', color: active ? 'var(--color-accent)' : 'var(--color-text-muted)' }} aria-hidden="true" />
                          <span style={{ fontSize: '14px', fontWeight: 500, color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}>
                            {item.label}
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>

                {/* Mobile: Channels section */}
                <div style={{ margin: '12px 0 4px', padding: '0 8px' }}>
                  <div style={{ height: '1px', backgroundColor: 'var(--color-border-subtle)' }} />
                  <span
                    style={{
                      display: 'block',
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'var(--color-text-muted)',
                      padding: '10px 4px 4px',
                    }}
                  >
                    Channels
                  </span>
                </div>
                <ul className="space-y-1">
                  {channels && channels.length > 0
                    ? channels.map((c) => {
                        const target = `/channels/${c.slug}`;
                        const active = location.pathname === target || location.pathname.startsWith(target + '/');
                        return (
                          <li key={c.slug}>
                            <Link
                              to={target}
                              aria-current={active ? 'page' : undefined}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 12px',
                                minHeight: '44px',
                                borderRadius: '10px',
                                borderLeft: active ? '3px solid var(--color-accent)' : '3px solid transparent',
                                backgroundColor: active ? 'var(--color-dark-elevated-2)' : 'transparent',
                                textDecoration: 'none',
                              }}
                            >
                              <TvIcon style={{ width: '20px', height: '20px', color: active ? 'var(--color-accent)' : 'var(--color-text-muted)' }} aria-hidden="true" />
                              <span style={{ fontSize: '14px', fontWeight: 500, color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {c.display_name}
                              </span>
                            </Link>
                          </li>
                        );
                      })
                    : null}
                  {/* Coaches/admins with no channels see a Create entry */}
                  {channels !== null && channels.length === 0 && user?.role !== 'athlete' && (
                    <li>
                      <Link
                        to="/channels/create"
                        aria-current={location.pathname === '/channels/create' ? 'page' : undefined}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 12px',
                          minHeight: '44px',
                          borderRadius: '10px',
                          borderLeft: channelActive ? '3px solid var(--color-accent)' : '3px solid transparent',
                          backgroundColor: channelActive ? 'var(--color-dark-elevated-2)' : 'transparent',
                          textDecoration: 'none',
                        }}
                      >
                        <PlusCircleIcon style={{ width: '20px', height: '20px', color: 'var(--color-text-muted)' }} aria-hidden="true" />
                        <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
                          Create channel
                        </span>
                      </Link>
                    </li>
                  )}
                </ul>

                {/* Mobile: Admin section */}
                {user?.role === 'admin' && (
                  <>
                    <div style={{ margin: '12px 0 4px', padding: '0 8px' }}>
                      <div style={{ height: '1px', backgroundColor: 'var(--color-border-subtle)' }} />
                      <span
                        style={{
                          display: 'block',
                          fontSize: '11px',
                          fontWeight: 600,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: 'var(--color-accent-gold)',
                          padding: '10px 4px 4px',
                          opacity: 0.8,
                        }}
                      >
                        Admin
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {[
                        { label: 'Overview', href: '/admin' },
                        { label: 'Users', href: '/admin/users' },
                        { label: 'Invitations', href: '/admin/invitations' },
                        { label: 'Metrics', href: '/admin/metrics' },
                      ].map(({ label, href }) => {
                        const active = location.pathname === href || location.pathname.startsWith(href + '/');
                        return (
                          <li key={href}>
                            <Link
                              to={href}
                              aria-current={active ? 'page' : undefined}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 12px',
                                minHeight: '44px',
                                borderRadius: '10px',
                                borderLeft: active ? '3px solid var(--color-accent-gold)' : '3px solid transparent',
                                backgroundColor: active ? 'color-mix(in srgb, var(--color-accent-gold) 8%, transparent)' : 'transparent',
                                textDecoration: 'none',
                              }}
                            >
                              <ShieldCheckIcon style={{ width: '20px', height: '20px', color: active ? 'var(--color-accent-gold)' : 'var(--color-text-muted)' }} aria-hidden="true" />
                              <span style={{ fontSize: '14px', fontWeight: 500, color: active ? 'var(--color-accent-gold)' : 'var(--color-text-secondary)' }}>
                                {label}
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}

                {/* Mobile: Coach Settings section */}
                {user?.role === 'coach' && (
                  <>
                    <div style={{ margin: '12px 0 4px', padding: '0 8px' }}>
                      <div style={{ height: '1px', backgroundColor: 'var(--color-border-subtle)' }} />
                      <span
                        style={{
                          display: 'block',
                          fontSize: '11px',
                          fontWeight: 600,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: 'var(--color-accent-blue)',
                          padding: '10px 4px 4px',
                          opacity: 0.85,
                        }}
                      >
                        Coach Settings
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {(() => {
                        const active = location.pathname === '/coach/invitations' || location.pathname.startsWith('/coach/invitations/');
                        return (
                          <li>
                            <Link
                              to="/coach/invitations"
                              aria-current={active ? 'page' : undefined}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 12px',
                                minHeight: '44px',
                                borderRadius: '10px',
                                borderLeft: active ? '3px solid var(--color-accent-blue)' : '3px solid transparent',
                                backgroundColor: active ? 'color-mix(in srgb, var(--color-accent-blue) 8%, transparent)' : 'transparent',
                                textDecoration: 'none',
                              }}
                            >
                              <EnvelopeIcon style={{ width: '20px', height: '20px', color: active ? 'var(--color-accent-blue)' : 'var(--color-text-muted)' }} aria-hidden="true" />
                              <span style={{ fontSize: '14px', fontWeight: 500, color: active ? 'var(--color-accent-blue)' : 'var(--color-text-secondary)' }}>
                                Invitations
                              </span>
                            </Link>
                          </li>
                        );
                      })()}
                      {channels && channels.length > 0 && (() => {
                        const active = location.pathname === '/athletes' || location.pathname.startsWith('/athletes/');
                        return (
                          <li>
                            <Link
                              to="/athletes"
                              aria-current={active ? 'page' : undefined}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 12px',
                                minHeight: '44px',
                                borderRadius: '10px',
                                borderLeft: active ? '3px solid var(--color-accent-blue)' : '3px solid transparent',
                                backgroundColor: active ? 'color-mix(in srgb, var(--color-accent-blue) 8%, transparent)' : 'transparent',
                                textDecoration: 'none',
                              }}
                            >
                              <UserGroupIcon style={{ width: '20px', height: '20px', color: active ? 'var(--color-accent-blue)' : 'var(--color-text-muted)' }} aria-hidden="true" />
                              <span style={{ fontSize: '14px', fontWeight: 500, color: active ? 'var(--color-accent-blue)' : 'var(--color-text-secondary)' }}>
                                My Athletes
                              </span>
                            </Link>
                          </li>
                        );
                      })()}
                    </ul>
                  </>
                )}
              </nav>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Sidebar;
